import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  SwatchIcon,
  SunIcon,
  MoonIcon,
  ComputerDesktopIcon,
  EyeIcon,
  PaintBrushIcon,
  SparklesIcon,
  AdjustmentsHorizontalIcon,
  SwatchIcon as ColorSwatchIcon,
  UserCircleIcon,
  Cog6ToothIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { SwatchIcon as SwatchIconSolid } from '@heroicons/react/24/solid';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const ThemeCustomizer = () => {
  const [theme, setTheme] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('appearance');
  const [previewMode, setPreviewMode] = useState(false);

  useEffect(() => {
    // Simulate fetching theme settings
    const fetchTheme = () => {
      const mockTheme = {
        // Appearance Settings
        appearance: {
          colorScheme: 'light',
          primaryColor: 'blue',
          accentColor: 'indigo',
          fontFamily: 'inter',
          fontSize: 'medium',
          borderRadius: 'medium',
          compactMode: false,
          highContrast: false,
          reducedMotion: false,
          systemTheme: false
        },

        // Layout Settings
        layout: {
          sidebarWidth: 'normal',
          headerStyle: 'default',
          contentWidth: 'full',
          cardStyle: 'elevated',
          spacing: 'comfortable',
          showBreadcrumbs: true,
          showPageTitles: true,
          stickyHeader: true,
          collapsibleSidebar: true,
          showQuickActions: true
        },

        // Typography Settings
        typography: {
          headingFont: 'inter',
          bodyFont: 'inter',
          monoFont: 'jetbrains-mono',
          lineHeight: 'normal',
          letterSpacing: 'normal',
          fontWeight: 'normal',
          headingScale: 'medium',
          textRendering: 'optimizeLegibility'
        },

        // Color Settings
        colors: {
          primary: '#3B82F6',
          secondary: '#6366F1',
          success: '#10B981',
          warning: '#F59E0B',
          error: '#EF4444',
          info: '#06B6D4',
          surface: '#FFFFFF',
          background: '#F9FAFB',
          text: '#111827',
          textSecondary: '#6B7280',
          border: '#E5E7EB'
        },

        // Advanced Settings
        advanced: {
          animations: true,
          transitions: true,
          shadows: true,
          blur: true,
          gradients: true,
          patterns: false,
          textures: false,
          customCSS: '',
          debugMode: false
        },

        // Accessibility Settings
        accessibility: {
          focusRing: true,
          highContrast: false,
          largeText: false,
          reduceMotion: false,
          screenReaderOptimized: false,
          keyboardNavigation: true,
          colorBlindFriendly: false,
          textSpacing: 'normal'
        },

        // Custom Themes
        customThemes: [
          {
            id: 'hospital-blue',
            name: 'Hospital Blue',
            description: 'Professional medical theme',
            colors: {
              primary: '#0066CC',
              secondary: '#0052A3',
              background: '#F8FAFC'
            }
          },
          {
            id: 'warm-sunset',
            name: 'Warm Sunset',
            description: 'Warm and welcoming theme',
            colors: {
              primary: '#F97316',
              secondary: '#EA580C',
              background: '#FEF7ED'
            }
          },
          {
            id: 'forest-green',
            name: 'Forest Green',
            description: 'Calming natural theme',
            colors: {
              primary: '#059669',
              secondary: '#047857',
              background: '#F0FDF4'
            }
          }
        ]
      };

      setTheme(mockTheme);
      setLoading(false);
    };

    setTimeout(fetchTheme, 500);
  }, []);

  const handleThemeChange = (category, setting, value) => {
    setSaving(true);
    
    setTimeout(() => {
      setTheme(prev => ({
        ...prev,
        [category]: {
          ...prev[category],
          [setting]: value
        }
      }));
      setSaving(false);
    }, 300);
  };

  const handleColorChange = (colorKey, value) => {
    setSaving(true);
    
    setTimeout(() => {
      setTheme(prev => ({
        ...prev,
        colors: {
          ...prev.colors,
          [colorKey]: value
        }
      }));
      setSaving(false);
    }, 300);
  };

  const applyCustomTheme = (customTheme) => {
    setSaving(true);
    
    setTimeout(() => {
      setTheme(prev => ({
        ...prev,
        colors: {
          ...prev.colors,
          ...customTheme.colors
        },
        appearance: {
          ...prev.appearance,
          primaryColor: 'custom'
        }
      }));
      setSaving(false);
    }, 500);
  };

  const resetToDefault = () => {
    setSaving(true);
    
    setTimeout(() => {
      setTheme(prev => ({
        ...prev,
        appearance: {
          ...prev.appearance,
          colorScheme: 'light',
          primaryColor: 'blue',
          accentColor: 'indigo',
          fontFamily: 'inter',
          fontSize: 'medium',
          borderRadius: 'medium',
          compactMode: false,
          highContrast: false,
          reducedMotion: false,
          systemTheme: false
        },
        colors: {
          primary: '#3B82F6',
          secondary: '#6366F1',
          success: '#10B981',
          warning: '#F59E0B',
          error: '#EF4444',
          info: '#06B6D4',
          surface: '#FFFFFF',
          background: '#F9FAFB',
          text: '#111827',
          textSecondary: '#6B7280',
          border: '#E5E7EB'
        }
      }));
      setSaving(false);
    }, 500);
  };

  const exportTheme = () => {
    const themeData = {
      name: 'My Custom Theme',
      version: '1.0.0',
      settings: theme
    };
    
    const dataStr = JSON.stringify(themeData, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = 'apollo-theme.json';
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const colorOptions = [
    { name: 'Blue', value: 'blue', color: '#3B82F6' },
    { name: 'Indigo', value: 'indigo', color: '#6366F1' },
    { name: 'Purple', value: 'purple', color: '#8B5CF6' },
    { name: 'Pink', value: 'pink', color: '#EC4899' },
    { name: 'Red', value: 'red', color: '#EF4444' },
    { name: 'Orange', value: 'orange', color: '#F97316' },
    { name: 'Yellow', value: 'yellow', color: '#EAB308' },
    { name: 'Green', value: 'green', color: '#10B981' },
    { name: 'Teal', value: 'teal', color: '#14B8A6' },
    { name: 'Cyan', value: 'cyan', color: '#06B6D4' }
  ];

  const fontOptions = [
    { name: 'Inter', value: 'inter' },
    { name: 'Roboto', value: 'roboto' },
    { name: 'Open Sans', value: 'open-sans' },
    { name: 'Poppins', value: 'poppins' },
    { name: 'Lato', value: 'lato' },
    { name: 'Source Sans Pro', value: 'source-sans-pro' }
  ];

  const tabs = [
    { id: 'appearance', name: 'Appearance', icon: SunIcon },
    { id: 'layout', name: 'Layout', icon: AdjustmentsHorizontalIcon },
    { id: 'typography', name: 'Typography', icon: PaintBrushIcon },
    { id: 'colors', name: 'Colors', icon: ColorSwatchIcon },
    { id: 'accessibility', name: 'Accessibility', icon: EyeIcon },
    { id: 'advanced', name: 'Advanced', icon: SparklesIcon }
  ];

  return (
    <div className="min-h-screen bg-apollo-gray">
      <Header />
      {/* Custom Header for Theme Customizer */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-4 sm:py-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
              <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0">
                <SwatchIconSolid className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600 sm:mr-3" />
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Theme Customizer</h1>
                  <p className="mt-1 sm:mt-2 text-sm text-gray-700">
                    Personalize your workspace appearance and experience
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 sm:space-x-3">
                <button
                  onClick={() => setPreviewMode(!previewMode)}
                  className={`inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md ${
                    previewMode 
                      ? 'text-blue-700 bg-blue-50 border-blue-300' 
                      : 'text-gray-700 bg-white hover:bg-gray-50'
                  }`}
                >
                  <EyeIcon className="h-4 w-4 mr-2" />
                  {previewMode ? 'Exit Preview' : 'Preview'}
                </button>
                <Link
                  to="/profile"
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <UserCircleIcon className="h-4 w-4 mr-2" />
                  Profile
                </Link>
                <Link
                  to="/profile/settings"
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <Cog6ToothIcon className="h-4 w-4 mr-2" />
                  Settings
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow mb-6 sm:mb-8">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
            <h3 className="text-base sm:text-lg font-medium text-gray-900">Quick Actions</h3>
            <p className="mt-1 text-sm text-gray-500">
              Common theme operations and presets
            </p>
          </div>
          <div className="px-4 sm:px-6 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <button
                onClick={resetToDefault}
                disabled={saving}
                className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                <ArrowPathIcon className="h-4 w-4 mr-2" />
                Reset to Default
              </button>
              <button
                onClick={exportTheme}
                className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <SwatchIcon className="h-4 w-4 mr-2" />
                Export Theme
              </button>
              <button
                onClick={() => handleThemeChange('appearance', 'colorScheme', theme.appearance.colorScheme === 'light' ? 'dark' : 'light')}
                className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                {theme.appearance.colorScheme === 'light' ? <MoonIcon className="h-4 w-4 mr-2" /> : <SunIcon className="h-4 w-4 mr-2" />}
                Toggle {theme.appearance.colorScheme === 'light' ? 'Dark' : 'Light'}
              </button>
              <button
                onClick={() => handleThemeChange('appearance', 'compactMode', !theme.appearance.compactMode)}
                className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <AdjustmentsHorizontalIcon className="h-4 w-4 mr-2" />
                {theme.appearance.compactMode ? 'Expand' : 'Compact'} Mode
              </button>
            </div>
          </div>
        </div>

        {/* Custom Theme Presets */}
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Theme Presets</h3>
            <p className="mt-1 text-sm text-gray-500">
              Pre-designed themes for quick application
            </p>
          </div>
          <div className="px-6 py-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {theme.customThemes.map((customTheme) => (
                <div
                  key={customTheme.id}
                  className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 cursor-pointer"
                  onClick={() => applyCustomTheme(customTheme)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium text-gray-900">{customTheme.name}</h4>
                    <div className="flex space-x-1">
                      <div
                        className="w-4 h-4 rounded-full border border-gray-300"
                        style={{ backgroundColor: customTheme.colors.primary }}
                      ></div>
                      <div
                        className="w-4 h-4 rounded-full border border-gray-300"
                        style={{ backgroundColor: customTheme.colors.secondary }}
                      ></div>
                      <div
                        className="w-4 h-4 rounded-full border border-gray-300"
                        style={{ backgroundColor: customTheme.colors.background }}
                      ></div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">{customTheme.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8 overflow-x-auto">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
              >
                <tab.icon className="h-4 w-4 mr-2" />
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {/* Appearance Tab */}
          {activeTab === 'appearance' && (
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Appearance Settings</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Control the overall look and feel of your interface
                </p>
              </div>
              <div className="px-6 py-6 space-y-6">
                {/* Color Scheme */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Color Scheme</label>
                  <div className="grid grid-cols-3 gap-3">
                    <button
                      onClick={() => handleThemeChange('appearance', 'colorScheme', 'light')}
                      className={`p-4 border-2 rounded-lg flex flex-col items-center ${
                        theme.appearance.colorScheme === 'light'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <SunIcon className="h-6 w-6 text-yellow-500 mb-2" />
                      <span className="text-sm font-medium">Light</span>
                    </button>
                    <button
                      onClick={() => handleThemeChange('appearance', 'colorScheme', 'dark')}
                      className={`p-4 border-2 rounded-lg flex flex-col items-center ${
                        theme.appearance.colorScheme === 'dark'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <MoonIcon className="h-6 w-6 text-blue-500 mb-2" />
                      <span className="text-sm font-medium">Dark</span>
                    </button>
                    <button
                      onClick={() => handleThemeChange('appearance', 'colorScheme', 'auto')}
                      className={`p-4 border-2 rounded-lg flex flex-col items-center ${
                        theme.appearance.colorScheme === 'auto'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <ComputerDesktopIcon className="h-6 w-6 text-gray-500 mb-2" />
                      <span className="text-sm font-medium">Auto</span>
                    </button>
                  </div>
                </div>

                {/* Primary Color */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Primary Color</label>
                  <div className="grid grid-cols-5 gap-3">
                    {colorOptions.map((color) => (
                      <button
                        key={color.value}
                        onClick={() => handleThemeChange('appearance', 'primaryColor', color.value)}
                        className={`p-3 border-2 rounded-lg flex flex-col items-center ${
                          theme.appearance.primaryColor === color.value
                            ? 'border-gray-800'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div
                          className="w-6 h-6 rounded-full mb-2"
                          style={{ backgroundColor: color.color }}
                        ></div>
                        <span className="text-xs font-medium">{color.name}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Font Size */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Font Size</label>
                  <select
                    value={theme.appearance.fontSize}
                    onChange={(e) => handleThemeChange('appearance', 'fontSize', e.target.value)}
                    className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="small">Small</option>
                    <option value="medium">Medium</option>
                    <option value="large">Large</option>
                    <option value="extra-large">Extra Large</option>
                  </select>
                </div>

                {/* Border Radius */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Border Radius</label>
                  <select
                    value={theme.appearance.borderRadius}
                    onChange={(e) => handleThemeChange('appearance', 'borderRadius', e.target.value)}
                    className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="none">None</option>
                    <option value="small">Small</option>
                    <option value="medium">Medium</option>
                    <option value="large">Large</option>
                    <option value="extra-large">Extra Large</option>
                  </select>
                </div>

                {/* Toggle Options */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">Compact Mode</h4>
                      <p className="text-xs text-gray-500">Reduce spacing and padding for denser layout</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={theme.appearance.compactMode}
                        onChange={(e) => handleThemeChange('appearance', 'compactMode', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">High Contrast</h4>
                      <p className="text-xs text-gray-500">Increase contrast for better visibility</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={theme.appearance.highContrast}
                        onChange={(e) => handleThemeChange('appearance', 'highContrast', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">Reduced Motion</h4>
                      <p className="text-xs text-gray-500">Minimize animations and transitions</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={theme.appearance.reducedMotion}
                        onChange={(e) => handleThemeChange('appearance', 'reducedMotion', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Layout Tab */}
          {activeTab === 'layout' && (
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Layout Settings</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Customize the layout and structure of your interface
                </p>
              </div>
              <div className="px-6 py-6 space-y-6">
                {/* Layout Selects */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Sidebar Width</label>
                    <select
                      value={theme.layout.sidebarWidth}
                      onChange={(e) => handleThemeChange('layout', 'sidebarWidth', e.target.value)}
                      className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="narrow">Narrow</option>
                      <option value="normal">Normal</option>
                      <option value="wide">Wide</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Header Style</label>
                    <select
                      value={theme.layout.headerStyle}
                      onChange={(e) => handleThemeChange('layout', 'headerStyle', e.target.value)}
                      className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="default">Default</option>
                      <option value="minimal">Minimal</option>
                      <option value="prominent">Prominent</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Content Width</label>
                    <select
                      value={theme.layout.contentWidth}
                      onChange={(e) => handleThemeChange('layout', 'contentWidth', e.target.value)}
                      className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="narrow">Narrow</option>
                      <option value="normal">Normal</option>
                      <option value="wide">Wide</option>
                      <option value="full">Full Width</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Card Style</label>
                    <select
                      value={theme.layout.cardStyle}
                      onChange={(e) => handleThemeChange('layout', 'cardStyle', e.target.value)}
                      className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="flat">Flat</option>
                      <option value="outlined">Outlined</option>
                      <option value="elevated">Elevated</option>
                    </select>
                  </div>
                </div>

                {/* Layout Toggles */}
                <div className="space-y-4">
                  {Object.entries(theme.layout)
                    .filter(([key]) => !['sidebarWidth', 'headerStyle', 'contentWidth', 'cardStyle', 'spacing'].includes(key))
                    .map(([key, value]) => (
                    <div key={key} className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">
                          {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                        </h4>
                        <p className="text-xs text-gray-500">
                          {key === 'showBreadcrumbs' && 'Display navigation breadcrumbs'}
                          {key === 'showPageTitles' && 'Show page titles in header'}
                          {key === 'stickyHeader' && 'Keep header visible when scrolling'}
                          {key === 'collapsibleSidebar' && 'Allow sidebar to be collapsed'}
                          {key === 'showQuickActions' && 'Display quick action buttons'}
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={value}
                          onChange={(e) => handleThemeChange('layout', key, e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Typography Tab */}
          {activeTab === 'typography' && (
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Typography Settings</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Customize fonts and text appearance
                </p>
              </div>
              <div className="px-6 py-6 space-y-6">
                {/* Font Selections */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Heading Font</label>
                    <select
                      value={theme.typography.headingFont}
                      onChange={(e) => handleThemeChange('typography', 'headingFont', e.target.value)}
                      className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {fontOptions.map(font => (
                        <option key={font.value} value={font.value}>{font.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Body Font</label>
                    <select
                      value={theme.typography.bodyFont}
                      onChange={(e) => handleThemeChange('typography', 'bodyFont', e.target.value)}
                      className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {fontOptions.map(font => (
                        <option key={font.value} value={font.value}>{font.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Monospace Font</label>
                    <select
                      value={theme.typography.monoFont}
                      onChange={(e) => handleThemeChange('typography', 'monoFont', e.target.value)}
                      className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="jetbrains-mono">JetBrains Mono</option>
                      <option value="fira-code">Fira Code</option>
                      <option value="source-code-pro">Source Code Pro</option>
                      <option value="monaco">Monaco</option>
                    </select>
                  </div>
                </div>

                {/* Typography Settings */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Line Height</label>
                    <select
                      value={theme.typography.lineHeight}
                      onChange={(e) => handleThemeChange('typography', 'lineHeight', e.target.value)}
                      className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="tight">Tight</option>
                      <option value="normal">Normal</option>
                      <option value="relaxed">Relaxed</option>
                      <option value="loose">Loose</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Letter Spacing</label>
                    <select
                      value={theme.typography.letterSpacing}
                      onChange={(e) => handleThemeChange('typography', 'letterSpacing', e.target.value)}
                      className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="tight">Tight</option>
                      <option value="normal">Normal</option>
                      <option value="wide">Wide</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Font Weight</label>
                    <select
                      value={theme.typography.fontWeight}
                      onChange={(e) => handleThemeChange('typography', 'fontWeight', e.target.value)}
                      className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="light">Light</option>
                      <option value="normal">Normal</option>
                      <option value="medium">Medium</option>
                      <option value="semibold">Semibold</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Heading Scale</label>
                    <select
                      value={theme.typography.headingScale}
                      onChange={(e) => handleThemeChange('typography', 'headingScale', e.target.value)}
                      className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="small">Small</option>
                      <option value="medium">Medium</option>
                      <option value="large">Large</option>
                      <option value="extra-large">Extra Large</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Colors Tab */}
          {activeTab === 'colors' && (
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Color Settings</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Customize the color palette for your interface
                </p>
              </div>
              <div className="px-6 py-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {Object.entries(theme.colors).map(([key, value]) => (
                    <div key={key}>
                      <label className="block text-sm font-medium text-gray-700 mb-2 capitalize">
                        {key.replace(/([A-Z])/g, ' $1')}
                      </label>
                      <div className="flex items-center space-x-3">
                        <input
                          type="color"
                          value={value}
                          onChange={(e) => handleColorChange(key, e.target.value)}
                          className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={value}
                          onChange={(e) => handleColorChange(key, e.target.value)}
                          className="flex-1 border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="#000000"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Accessibility Tab */}
          {activeTab === 'accessibility' && (
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Accessibility Settings</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Improve accessibility and usability for all users
                </p>
              </div>
              <div className="px-6 py-6 space-y-4">
                {Object.entries(theme.accessibility).map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">
                        {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                      </h4>
                      <p className="text-xs text-gray-500">
                        {key === 'focusRing' && 'Show focus indicators for keyboard navigation'}
                        {key === 'highContrast' && 'Increase contrast between text and background'}
                        {key === 'largeText' && 'Use larger text sizes throughout the interface'}
                        {key === 'reduceMotion' && 'Minimize animations and motion effects'}
                        {key === 'screenReaderOptimized' && 'Optimize for screen reader users'}
                        {key === 'keyboardNavigation' && 'Enable full keyboard navigation support'}
                        {key === 'colorBlindFriendly' && 'Use color-blind friendly color combinations'}
                        {key === 'textSpacing' && 'Increase spacing between text elements'}
                      </p>
                    </div>
                    {key === 'textSpacing' ? (
                      <select
                        value={value}
                        onChange={(e) => handleThemeChange('accessibility', key, e.target.value)}
                        className="block border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="normal">Normal</option>
                        <option value="comfortable">Comfortable</option>
                        <option value="spacious">Spacious</option>
                      </select>
                    ) : (
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={value}
                          onChange={(e) => handleThemeChange('accessibility', key, e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Advanced Tab */}
          {activeTab === 'advanced' && (
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Advanced Settings</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Advanced customization options for power users
                </p>
              </div>
              <div className="px-6 py-6 space-y-6">
                {/* Effect Toggles */}
                <div className="space-y-4">
                  {Object.entries(theme.advanced)
                    .filter(([key]) => !['customCSS', 'debugMode'].includes(key))
                    .map(([key, value]) => (
                    <div key={key} className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">
                          {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                        </h4>
                        <p className="text-xs text-gray-500">
                          {key === 'animations' && 'Enable smooth animations throughout the interface'}
                          {key === 'transitions' && 'Enable smooth transitions between states'}
                          {key === 'shadows' && 'Add depth with drop shadows and elevation'}
                          {key === 'blur' && 'Use blur effects for overlays and backdrops'}
                          {key === 'gradients' && 'Allow gradient backgrounds and effects'}
                          {key === 'patterns' && 'Enable decorative background patterns'}
                          {key === 'textures' && 'Add subtle textures to surfaces'}
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={value}
                          onChange={(e) => handleThemeChange('advanced', key, e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  ))}
                </div>

                {/* Custom CSS */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Custom CSS</label>
                  <textarea
                    value={theme.advanced.customCSS}
                    onChange={(e) => handleThemeChange('advanced', 'customCSS', e.target.value)}
                    rows={6}
                    className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 font-mono"
                    placeholder="/* Add your custom CSS here */"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Advanced users can add custom CSS to override default styles
                  </p>
                </div>

                {/* Debug Mode */}
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">Debug Mode</h4>
                    <p className="text-xs text-gray-500">Show debug information and development tools</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={theme.advanced.debugMode}
                      onChange={(e) => handleThemeChange('advanced', 'debugMode', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Saving Indicator */}
      {saving && (
        <div className="fixed bottom-4 right-4 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
          Saving...
        </div>
      )}
      
      <Footer />
    </div>
  );
};

export default ThemeCustomizer;
